import { useMemo } from 'react';
import { countWords } from '@historyjki/shared';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';

interface SplitStoryDialogProps {
  open: boolean;
  /** Interior spreads that will be rebuilt by the split. */
  existingSpreadCount: number;
  storyText: string;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Confirm dialog for beat detection. There is no page-count slider
 * anymore: the AI finds the story's natural beats regardless of length,
 * and the author groups them into pages on the Compose story rail —
 * where each page shows its real fill at the book's font size.
 */
export function SplitStoryDialog({
  open,
  existingSpreadCount,
  storyText,
  loading,
  onCancel,
  onConfirm,
}: SplitStoryDialogProps) {
  const wordCount = useMemo(() => countWords(storyText), [storyText]);

  return (
    <Modal open={open} onClose={loading ? () => {} : onCancel} title="Find the story's beats">
      <div className="split-dialog">
        <p className="split-dialog__intro">
          The AI reads your {wordCount.toLocaleString()}-word story and marks
          its natural beats — scene changes, turns, reveals — each with the
          key moment for its illustration. You&rsquo;re never asked for a page
          count: group beats into pages on the story rail below the canvas,
          where every page shows how full it is at your font size and how
          much room is left for art.
        </p>

        <ul className="split-dialog__points">
          <li>
            <strong>Beats, not pages.</strong> A beat is the smallest span
            that works as one illustration — usually a sentence or a few.
          </li>
          <li>
            <strong>You set the density.</strong> Fewer beats on a page →
            picture-led; more beats → text-led. Retune anytime without
            re-running the AI.
          </li>
          <li>
            <strong>Moments follow beats.</strong> Every beat&rsquo;s key
            moment lands in your generate list, whatever page it ends up on.
          </li>
        </ul>

        {existingSpreadCount > 0 && (
          <div className="split-dialog__warning" role="alert">
            This will rebuild the {existingSpreadCount} existing{' '}
            {existingSpreadCount === 1 ? 'spread' : 'spreads'} and the story
            moments. Your image bank is kept — every generated image stays
            available to place.
          </div>
        )}

        <div className="split-dialog__actions">
          <Button variant="ghost" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onConfirm} loading={loading}>
            Find beats
          </Button>
        </div>
      </div>
    </Modal>
  );
}
